import type { StyleName, ThemeName } from "@dentacore/ui";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

const STORAGE_KEY = "dentacore.theme";
const STYLE_KEY = "dentacore.style";

interface ThemeState {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  /**
   * The style the person chose (CF-D6) — Solid unless they picked Glass (SD-D1). What
   * is *drawn* can still be Solid when the browser cannot blur or the person
   * has asked their OS for less transparency; `data-style` on `<html>` says
   * which, and is what CSS should key on.
   */
  style: StyleName;
  setStyle: (style: StyleName) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

function stored(): ThemeName | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

function storedStyle(): StyleName | null {
  try {
    const value = localStorage.getItem(STYLE_KEY);
    return value === "glass" || value === "solid" ? value : null;
  } catch {
    return null;
  }
}

/**
 * Whether Glass can be drawn here at all. Without `backdrop-filter` a pane is
 * just a translucent wash over a gradient — the legibility risk with none of
 * the frosting — and a person who has asked for reduced transparency has said
 * they do not want it. Either way the page falls back to Solid, so every
 * `[data-style="glass"]` rule in every stylesheet falls back with it.
 */
function glassAvailable(): boolean {
  try {
    const blurs =
      CSS.supports("backdrop-filter", "blur(1px)") ||
      CSS.supports("-webkit-backdrop-filter", "blur(1px)");
    const reduced = window.matchMedia?.("(prefers-reduced-transparency: reduce)").matches ?? false;
    return blurs && !reduced;
  } catch {
    return false;
  }
}

/**
 * Light and dark, chosen explicitly and remembered — and since CF-D6, Glass or
 * Solid beside them, remembered the same way.
 *
 * The mockup offers exactly these two and defaults to light, so this matches it
 * rather than adding a third "match my system" option — the token layer would
 * support one (light is the bare `:root`), but the mockup is the spec and
 * inventing a control it does not have is not parity.
 *
 * The attributes are written to `<html>` rather than a wrapper element so they
 * also cover anything portalled out of the React tree, and so the page
 * background changes rather than just the content's.
 */
export function ThemeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [theme, setThemeState] = useState<ThemeName>(() => stored() ?? "light");
  // **Solid is the default** (SD-D1, Emile 2026-10-21, reversing CF-D6's Glass);
  // Glass is one switch away in the account menu, and a choice already made stands.
  const [style, setStyleState] = useState<StyleName>(() => storedStyle() ?? "solid");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    const drawn: StyleName = style === "glass" && glassAvailable() ? "glass" : "solid";
    document.documentElement.setAttribute("data-style", drawn);
  }, [style]);

  const setTheme = useCallback((next: ThemeName) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Choice simply does not survive the tab. Not worth failing over.
    }
  }, []);

  const setStyle = useCallback((next: StyleName) => {
    setStyleState(next);
    try {
      localStorage.setItem(STYLE_KEY, next);
    } catch {
      // As for the theme: the choice lasts this tab only.
    }
  }, []);

  const value = useMemo<ThemeState>(
    () => ({ theme, setTheme, style, setStyle }),
    [theme, setTheme, style, setStyle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const context = useContext(ThemeContext);
  if (context === null) throw new Error("useTheme must be used inside <ThemeProvider>");
  return context;
}
